import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Check, Phone, Mail, MapPin } from 'lucide-react';
import { company, cityData, cityServices, citySlugs } from '../mock';
import Seo from '../components/Seo';
import StructuredData from '../components/StructuredData';
import { PageFaq } from '../sections/RestSections';

const slugFor = (name) => citySlugs.find((s) => cityData[s].name === name);

export default function CityPage() {
  const { city } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, [city]);
  const data = cityData[city];
  if (!data) return <Navigate to="/service-areas" replace />;

  return (
    <main className="page services-page city-page" data-testid={`city-page-${city}`}>
      <Seo
        title={`Plumber in ${data.name}, GA`}
        description={`Licensed plumber serving ${data.name}, GA. Victory Plumbing offers water heaters, drain cleaning, sewer & water lines, repipes and more. Family owned. Call ${company.phoneDisplay}.`}
        path={`/service-areas/${city}`}
      />
      <StructuredData faq={false} />

      <section className="page-hero">
        <div className="wrap">
          <p className="eyebrow">Service Area</p>
          <h1>Plumber in {data.name}, GA</h1>
          <p className="lede">{data.intro}</p>
          <div className="about-cta">
            <a className="btn btn-gold" href={company.phoneHref}><Phone size={16} /> Call {company.phoneDisplay}</a>
            <Link className="btn btn-outline-dark" to="/contact"><Mail size={15} /> Request Service</Link>
          </div>
        </div>
      </section>

      <section className="landing-body">
        <div className="wrap">
          <div className="landing-grid">
            <div>
              <h2>Plumbing services in {data.name}</h2>
              <div className="val-grid">
                {cityServices.map((s) => (
                  <div className="val-item" key={s}><span className="ic"><Check size={18} /></span><span>{s}</span></div>
                ))}
              </div>
            </div>
            <aside className="landing-side">
              <div className="landing-signs">
                <h3>Local, on the road in {data.name}</h3>
                <p style={{ color: '#4a4638', fontSize: '15px', lineHeight: 1.7 }}>{data.note}</p>
                <p style={{ marginTop: '14px', fontSize: '14px', color: '#6b6555' }}>
                  <strong>Zip codes served:</strong> {data.zips}
                </p>
              </div>
            </aside>
          </div>

          {data.nearby && data.nearby.length > 0 && (
            <div className="landing-areas">
              <h3>We also serve nearby</h3>
              <div className="area-chip-grid">
                {data.nearby.map((n) => {
                  const s = slugFor(n);
                  return s
                    ? <Link className="area-chip area-chip-link" key={n} to={`/service-areas/${s}`} data-testid={`area-link-${s}`}><MapPin size={15} />{n}</Link>
                    : <div className="area-chip" key={n}><MapPin size={15} />{n}</div>;
                })}
              </div>
            </div>
          )}

          <div className="blog-cta">
            <div>
              <h3>Need a plumber in {data.name}?</h3>
              <p>Call now or send a message &mdash; we&rsquo;ll get you scheduled fast.</p>
            </div>
            <div className="blog-cta-actions">
              <a className="btn btn-gold" href={company.phoneHref}><Phone size={16} /> Call {company.phoneDisplay}</a>
              <Link className="btn btn-outline-dark" to="/contact"><Mail size={16} /> Request Service</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="city-map-section" data-testid={`city-map-${city}`}>
        <div className="wrap">
          <div className="sec-head" style={{ marginBottom: '22px' }}>
            <p className="eyebrow">On the Map</p>
            <h2>Serving {data.name}, GA</h2>
          </div>
          <div className="city-map">
            <iframe
              title={`Map of ${data.name}, GA`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(data.name + ', GA')}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <PageFaq
        title={`${data.name} plumbing FAQs`}
        subtitle={`Common questions from ${data.name} homeowners and businesses.`}
        items={data.faqs}
        testid={`city-faqs-${city}`}
      />
    </main>
  );
}
